import Vue from 'vue';
import {
  getRoomModel,
  getPassengerModel
} from './models';
import merge from 'lodash/merge';

export default {
  setIsCheckoutPage(state, isCheckoutPage) {
    state.isCheckoutPage = isCheckoutPage;
  },

  setLoading(state, loading) {
    state.loading = loading;
  },

  setPaymentLoading(state, paymentLoading) {
    state.paymentLoading = paymentLoading;
  },

  setSteps(state, steps) {
    state.steps = steps;
  },

  nextStep(state, stepAmount = 1) {
    state.stepIndex = state.stepIndex + stepAmount;
  },

  prevStep(state, stepAmount = 1) {
    state.stepIndex = state.stepIndex - stepAmount;
  },

  setData(state, data) {
    state.data = {
      ...state.data,
      ...data
    };
  },

  setQuote(state, quote) {
    state.quote = {
      ...state.quote,
      ...quote
    };
  },

  setQuoteDonations(state) {
    if (state.quote.donations) {
      state.quote.donations.forEach((donation, index) => {
        Vue.set(state.quote.donations[index], 'continent', state.continent);
        Vue.set(state.quote.donations[index], 'country', state.country);
        Vue.set(state.quote.donations[index], 'quantity', state.quote.passengers.filter(p => p.donated).length);
      });
    }
  },

  /** check if the quote.subtotal from API matches our runningSubtotal */
  setSubtotalMismatch(state, runningSubtotal) {
    state.subtotalMismatch = state.quote.subtotal !== runningSubtotal;
  },

  setTourCode(state, tourCode) {
    state.quote.tourCode = tourCode;
  },

  /** Reset the tour code to the initial tour code */
  resetTourCode(state) {
    state.quote.tourCode = state.quote.initialTourCode;
  },

  setTourName(state, tourName) {
    state.data.tourName = tourName;
  },

  setQuoteUrl(state, quoteUrl) {
    state.quote.quoteUrl = quoteUrl;
  },

  setDepartureId(state, departureId) {
    state.quote.departureId = departureId;
  },

  setDepartureTransportCode(state, departureTransportCode) {
    state.quote.departureTransportCode = departureTransportCode;
  },

  setAddonData(state, addonData) {
    state.addonData = addonData;
  },

  setDepartureDetails(state, departureDetails) {
    state.departureDetails = {
      ...departureDetails
    };
  },

  setAvailableRooms(state, availableRooms) {
    state.availableRooms = availableRooms;
  },

  setMaxiumumPassengers(state, maxAvailable) {
    if (maxAvailable < 9) {
      state.maxPassengers = maxAvailable;
    }
  },


  addRoom(state) {
    state.rooms.push(getRoomModel());
  },

  updateRoom(state, {
    room,
    index
  }) {

    const currentRoom = state.rooms[index];
    const currentPassengers = currentRoom.passengers;
    let newPassengers = [];

    // if room.occupancy is less than the currentRoom.occupancy
    if (room.occupancy < currentRoom.occupancy) {
      // simply modify the passenger array rather than replacing it
      newPassengers = currentPassengers.slice(0, room.occupancy);
    }

    // if room.occupancy is more than the currentRoom.occupancy
    else if (room.occupancy > currentRoom.occupancy) {
      // add new items to the passenger array
      newPassengers = [...currentPassengers];
      for (let i = currentRoom.occupancy; i < room.occupancy; i++) {
        newPassengers.push(getPassengerModel());
      }
    }

    // new room
    else {
      newPassengers = new Array(room.occupancy).fill(null).map(() => (getPassengerModel()));
    }

    Vue.set(state.rooms, index, {
      ...room,
      passengers: newPassengers
    });
  },

  removeRoom(state, index) {
    state.rooms.splice(index, 1);
  },

  setRoomPassengerId(state, payload) {
    Vue.set(payload.roomPassenger, 'passengerId', payload.passengerId);
  },

  excursionsVisited(state) {
    state.excursionsVisited = true;
  },

  excursionsExist(state) {
    state.excursionsExist = true;
  },

  resetExcursions(state) {
    state.excursions = [];
  },

  togglePassengerExcursion(state, {
    passenger,
    excursion
  }) {

    // TODO: cleanup (we might be able to simplify the data like below)
    /**
     * {
     *  [Excursion Code]: [Passenger Id, Passenger Id]
     * }
     */

    // if excursion exists
    // toggle the selected value of the passenger
    // otherwise, add the excursion and passenger to array
    const excursionIndex = state.excursions.findIndex(e => e.id === excursion.id);
    if (excursionIndex > -1) {

      // check if passenger exists in excursion already
      const passengerIndex = state.excursions[excursionIndex].passengers.findIndex(p => p.passengerId === passenger.passengerId);
      if (passengerIndex > -1) {
        // toggle passenger
        const pas = state.excursions[excursionIndex].passengers[passengerIndex];
        Vue.set(state.excursions[excursionIndex].passengers, passengerIndex, {
          ...pas,
          selected: !pas.selected
        });
      } else {
        // add passenger
        Vue.set(
          state.excursions[excursionIndex],
          'passengers',
          [...state.excursions[excursionIndex].passengers, {
            ...passenger,
            selected: true
          }]
        );
      }
    } else {
      state.excursions.push({
        ...excursion,
        passengers: [{
          ...passenger,
          selected: true
        }]
      });
    }
  },

  setExcursionData(state, excursionData) {
    state.excursionData = excursionData;
  },

  setDonationsData(state, donationsData) {
    state.donationsData = donationsData;
  },

  addNonDonatingTraveller(state, nonDonatingTraveller) {
    state.nonDonatingTravellers.push(nonDonatingTraveller);
  },

  removeNonDonatingTraveller(state, nonDonatingTraveller) {    
    const indexOfElementToRemove = state.nonDonatingTravellers.indexOf(nonDonatingTraveller);
    
    state.nonDonatingTravellers.splice(indexOfElementToRemove, 1);
  },

  setBookingDonation(state) {
    if (state.donationPerBooking == null) {
      state.donationPerBooking = state.donationsData.allocationType == 'perBooking';
      return;
    }

    state.donationPerBooking = !state.donationPerBooking;
  },

  setDonationMade(state, donationMade) {
    state.donationMade = donationMade;
  },

  setVisas(state, visas) {
    state.visas = visas;
  },

  setPassengersFromQuote(state) {
    const passengersFromQuote = [...state.quote.passengers];

    if (state.loggedInPassengerData) {
      const {
        passengerId,
        roomId,
        visaIds,
        ...otherPassengerData
      } = state.loggedInPassengerData;

      const passengerIndex = passengersFromQuote.findIndex(({
        firstName,
        lastName
      }) => firstName.toUpperCase() === otherPassengerData.firstName.toUpperCase() && lastName.toUpperCase() === otherPassengerData.lastName.toUpperCase());

      if (passengerIndex > -1) {
        passengersFromQuote[passengerIndex] = {
          ...otherPassengerData,
          ...passengersFromQuote[passengerIndex]
        };
      }
    }


    state.passengers = passengersFromQuote;
  },

  setloggedInPassengerData(state, userData) {
    state.loggedInPassengerData = userData;
  },

  resetPassengers(state) {
    state.passengers = [];
  },

  setPayment(state, payment) {
    state.payment = payment;
  },

  resetPayment(state) {
    state.paymentLoading = false;
    state.payment = null;
  },

  setPaymentFrame(state, paymentFrame) {
    state.paymentFrame = paymentFrame;
  },

  setBookingReference(state, bookingReference) {
    state.bookingReference = bookingReference;
  },

  setBooking(state, booking) {
    state.booking = booking;
  },

  setError(state, error) {
    state.loading = false;
    state.error = error;
  },

  setErrorMessage(state, errorMessage) {
    state.errorMessage = errorMessage;
  },

  setIsLoginBoxDisplayed(state, isLoginBoxDisplayed) {
    state.isLoginBoxDisplayed = isLoginBoxDisplayed;
  },

  toggleTakeFinalBalance(state) {
    state.quote.takeFinalBalance = !state.quote.takeFinalBalance;
  },

  setDataExcursions(state, data) {
    state.excursions.forEach((excursion, index) => {
      const passengersCount = excursion.passengers.length;
      Vue.set(state.excursions[index], 'continent', data.tourContinent);
      Vue.set(state.excursions[index], 'country', data.tourCountry);
      Vue.set(state.excursions[index], 'quantity', passengersCount);
    });

    state.excursions = state.excursions.filter(excursion => {
      return excursion.passengers.some(passenger => passenger.selected);
    });
  },

  setRoomsData(state, data) {
    state.rooms.forEach((room, index) => {
      Vue.set(state.rooms[index], 'continent', data.tourContinent);
      Vue.set(state.rooms[index], 'country', data.tourCountry);
      Vue.set(state.rooms[index], 'quantity', room.occupancy);
      Vue.set(state.rooms[index], 'price', room.pricePerPerson);
      Vue.set(state.rooms[index], 'variant', room.name);
    });
  },

  setPaymentType(state, paymentType) {
    state.paymentType = paymentType;
  },
 
  setTransactionType(state, transactionType) {
    state.transactionType = transactionType;
  },

  setToursData(state) {
    const newTour = {
      brand: state.data.brand,
      continent: state.data.tourContinent,
      country: state.data.tourCountry,
      discount: state.data.discount,
      quantity: 1,
      price: state.data.price,
      id: state.quote.tourCode,
      name: state.data.tourName
    };
    state.tours.push(newTour);
  },

  setDonations(state) {
    if(state.donationsData) {
      const quantity = state.quote.passengers.filter(passenger => passenger.donated).length;
      const newDonation = {
        brand: state.donationsData.brandName,
        id: state.quote.donationIds[0],
        name: state.donationsData.name,
        price: state.donationsData.price,
        quantity: quantity
      };
      
      if(quantity) {
        state.donations.push(newDonation);
      }
    }
  },
  
  setAddons(state) {
    const newAddons = state.addonData.addOns.map(addOn => ({
      continent: state.data.tourContinent,
      country: state.data.tourCountry,
      name: addOn.name,
      price: addOn.price,
      id: addOn.code
    }));
    
    state.addons.push(...newAddons);
  },

  setFlights(state) {
    const flights = state.departureDetails;
    
    const mapLegsToFlight = (legs) => {
      return legs.map(leg => ({
        brand: leg.flightCarrier.name,
        name: `flights(${leg.departureAirport.code}/${leg.arrivalAirport.code})`,
        id: leg.instanceCode,
        quantity: state.quote.passengers.length
      }));
    };

    const departureFlights = mapLegsToFlight(flights.departureLegs);
    const returnFlights = mapLegsToFlight(flights.returnLegs);

    state.flights = [...departureFlights, ...returnFlights];
  }
}
